import { FormikErrors, useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'
import { AddressFields } from 'routes/Checkout/Components/PostcodeAddressFields'
import {
  getIsBillingAddressDifferent,
  getBillingAddress,
} from 'routes/Checkout/checkoutPaymentSelectors'
import { getBasketPostcode } from 'selectors/basket'

export type FormFields = {
  isBillingAddressDifferent: boolean
  cardHolderName: string
} & AddressFields

const POSTCODE_MIN_LENGTH = 5
const POSTCODE_MAX_LENGTH = 8
const HOUSE_NUMBER_MAX_LENGTH = 200
const STREET_MAX_LENGTH = 200
const TOWN_MAX_LENGTH = 200

const validate = (formValues: FormFields): FormikErrors<FormFields> => {
  const errors: FormikErrors<FormFields> = {}
  if (!formValues.cardHolderName) {
    errors.cardHolderName = 'Card name is required'
  }
  if (formValues.isBillingAddressDifferent) {
    if (formValues.searchPostcode.length < POSTCODE_MIN_LENGTH) {
      errors.searchPostcode = 'Postcode must be at least 5 characters'
    } else if (formValues.searchPostcode.length > POSTCODE_MAX_LENGTH) {
      errors.searchPostcode = 'Postcode must be under 8 characters'
    }

    if (!formValues.houseNo) {
      errors.houseNo = 'House number is required'
    } else if (formValues.houseNo.length > HOUSE_NUMBER_MAX_LENGTH) {
      errors.houseNo = 'House number must be under 200 characters'
    }

    if (formValues.street.length > STREET_MAX_LENGTH) {
      errors.street = 'Street must be under 200 characters'
    }

    if (!formValues.town) {
      errors.town = 'City is required'
    } else if (formValues.town.length > TOWN_MAX_LENGTH) {
      errors.town = 'City must be under 200 characters'
    }
  }

  return errors
}

export const useAddressDetailsForm = () => {
  const deliveryPostcode = useSelector(getBasketPostcode)
  const billingAddress = useSelector(getBillingAddress)
  const isBillingAddressDifferent = useSelector(getIsBillingAddressDifferent)
  const dispatch = useDispatch()

  return useFormik<FormFields>({
    initialValues: {
      cardHolderName: '',
      searchPostcode: deliveryPostcode ?? '',
      postcode: billingAddress?.get('postcode') ?? '',
      houseNo: billingAddress?.get('houseNo') ?? '',
      street: billingAddress?.get('street') ?? '',
      town: billingAddress?.get('town') ?? '',
      county: billingAddress?.get('county') ?? '',
      udprn: billingAddress?.get('udprn') ?? '',
      isBillingAddressDifferent,
    },
    validateOnBlur: true,
    validate,
    onSubmit: (formValues) => {
      dispatch({
        type: actionTypes.PAYMENT_SET_BILLING_ADDRESS,
        postcode: formValues.postcode,
        houseNo: formValues.houseNo,
        street: formValues.street,
        town: formValues.town,
        county: formValues.county,
        udprn: formValues.udprn,
      })
    },
  })
}
